import React, { Component } from 'react'
import { select as d3select } from 'd3-selection'
import { zoom as d3zoom, zoomIdentity } from 'd3-zoom'
import debugConstructor from 'debug'
import isFinite from 'lodash/isFinite'
import some from 'lodash/some'
import * as PIXI from 'pixi.js-legacy'
import PropTypes from 'prop-types'
import * as TWEEN from 'tween.js'
import { isMobile } from '@manychat/manyui'

import { isFirefox as getIsFirefox } from 'utils'
import { BUILDER_CANVAS_ID } from 'common/builder/components/chart/FlowChart/constants'
import { isTestEnv } from 'utils/e2e'
import localStorage from 'utils/localStorage'
import HighlightService from 'utils/services/highlight/HighlightService'

import app from './app'
import { texts } from './Camera/ManagedText'
import { applyTextResolution } from './Camera/textManager'
import { appScaleExtent, STATES } from './config'
import PixiObject from './pixi-object'
import { getUserPlatform, manageFixedArray } from './utils'

import 'utils/lib/pixi-utils'

const debug = debugConstructor('PixiStage')

const TEXT_RESOLUTION_CHANGES_PER_FRAME_LIMIT = 30
const LAST_EVENTS_LIMIT = 10
const DETECTED_DEVICES_LIMIT = 20
const isFirefox = getIsFirefox()
const userPlatform = getUserPlatform()

let currentTouchesCount = 0
let isMultitouch = false
let wheelType = localStorage.getItem('mcht_wheel_type') || 'trackpad'
let lastEvents = []
let detectedDevices = []

class PixiStage extends Component {
  static propTypes = {
    width: PropTypes.number,
    height: PropTypes.number,
    onReady: PropTypes.func,
    onTransform: PropTypes.func,
    isStaticTextResolution: PropTypes.bool,
  }

  static defaultProps = {
    width: 800,
    height: 600,
    onReady: PixiObject.noop,
    onTransform: PixiObject.noop,
    isStaticTextResolution: false,
  }

  state = {
    ready: false,
    offsetX: 0,
    offsetY: 0,
  }

  transform = { k: 0.3, x: 0, y: 0 }

  zoomBlockers = []
  calculateWheelType = () => {
    if (lastEvents.length !== LAST_EVENTS_LIMIT) return

    const anyX = lastEvents.some((val) => val.deltaX !== 0)
    const averageTimeBetweenEvents =
      lastEvents.reduce((acc, val, i, arr) => {
        if (i === 0) return acc
        return acc + val.timeStamp - arr[i - 1].timeStamp
      }, 0) / lastEvents.length

    const averageWheelDeltaY =
      lastEvents.reduce((acc, val) => acc + Math.abs(val.wheelDeltaY), 0) / LAST_EVENTS_LIMIT
    const averageDeltaY =
      lastEvents.reduce((acc, val) => acc + Math.abs(val.deltaY), 0) / LAST_EVENTS_LIMIT
    const isWheelDeltaYConstant = lastEvents.every(
      (val) => Math.abs(val.wheelDeltaY) === averageWheelDeltaY,
    )

    const value = Math.round(averageWheelDeltaY * averageTimeBetweenEvents)
    const maximumForTrackpad = isFirefox ? 4000 : 5000
    const maximumForWindowsTrackpad = isFirefox ? 2000 : 1000

    let currentWheelType
    if (userPlatform === 'win' && !isFirefox) {
      // in windows chromium, wheelDeltaY is constant for mouse
      currentWheelType =
        (isWheelDeltaYConstant && averageWheelDeltaY !== 0) || value > maximumForWindowsTrackpad
          ? 'mouse'
          : 'trackpad'
    } else if (userPlatform === 'win' && isFirefox) {
      // in windows firefox, deltaY is constant for mouse
      currentWheelType =
        (isWheelDeltaYConstant && averageWheelDeltaY !== 0) || value > maximumForWindowsTrackpad
          ? 'mouse'
          : 'trackpad'
    } else {
      // in macos, we need to check the value of the product of averageWheelDeltaY and averageTimeBetweenEvents
      // thats the only way to detect the mouse
      // we also need to check if any of the events have deltaX (for mouse you can't have deltaX)
      currentWheelType = value > maximumForTrackpad && !anyX ? 'mouse' : 'trackpad'
    }

    // push detected device to the array and check isMouse by the ratio of mouse detection
    // we want to collect detectedDevices to exclude false positives
    // 80% of the detected devices should be mouse to consider it as a mouse
    manageFixedArray(detectedDevices, currentWheelType, DETECTED_DEVICES_LIMIT)
    const amountOfMouseDetection = detectedDevices.filter((val) => val === 'mouse').length
    const isMouse = amountOfMouseDetection / detectedDevices.length >= 0.8

    if (isMouse) {
      wheelType = 'mouse'
      localStorage.setItem('mcht_wheel_type', wheelType)
    } else {
      wheelType = 'trackpad'
      localStorage.setItem('mcht_wheel_type', wheelType)
    }

    debug('calculate wheel type', { averageWheelDeltaY, averageDeltaY, value, wheelType })
  }

  handleNavigation = (event) => {
    // we don't want to do nor navigation nor calculate wheel type if ctrl key is pressed, it's just zooms
    if (event.ctrlKey || event.metaKey) {
      return
    }

    const haveSomeEvents = lastEvents.length !== 0

    if (haveSomeEvents && event.timeStamp - lastEvents[lastEvents.length - 1].timeStamp >= 500) {
      lastEvents = []
    } else {
      // we don't want calculate based on crazy fast scrolling
      if (Math.abs(event.wheelDeltaY) < 400) {
        manageFixedArray(lastEvents, event, LAST_EVENTS_LIMIT)
        this.calculateWheelType()
      }
    }

    // event fires since we listen to wheel event for navigating via touchpad
    // but we don't want to do navigation if its wheel event of the mouse
    if (this.isMouseWheelEvent(event)) return

    app.transform.x -= event.deltaX
    app.transform.y -= event.deltaY

    this.updateTransform()
  }

  addBlocker = (blocker) => this.zoomBlockers.push(blocker)

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.ready && this.state.ready) {
      this.props.onReady?.()
      this.setupUITests()
    }

    if (app.root == null || app.root.renderer == null) {
      return
    }

    if (this.props.width !== prevProps.width || this.props.height !== prevProps.height) {
      const { width, height } = this.props
      app.root.renderer.resize(width, height)
      app.width = width
      app.height = height
      this.setState({ offsetX: this.getOffset().x, offsetY: this.getOffset().y })
    }

    if (this.props.readonly !== prevProps.readonly) {
      app.root.stage.interactive = !this.props.readonly
    }
  }

  getOffset = () => {
    // sidebars are not part of the stage
    return {
      x: window.innerWidth - this.props.width,
      y: window.innerHeight - this.props.height,
    }
  }

  handlePreventDefault = (event) => {
    event.preventDefault()
  }

  updateTransform = () => {
    const { stage } = app.root
    let changed

    if (stage.position.x !== app.transform.x) {
      stage.position.x = app.transform.x
      changed = true
    }
    if (stage.position.y !== app.transform.y) {
      stage.position.y = app.transform.y
      changed = true
    }
    if (stage.scale.x !== app.transform.k) {
      stage.scale.x = app.transform.k
      stage.scale.y = app.transform.k
      changed = true
    }

    if (changed) {
      app.render()
    }
  }

  isMouseWheelEvent = () => wheelType === 'mouse'

  componentDidMount() {
    app.width = this.props.width
    app.height = this.props.height
    app.transform = {}
    app.updateTransform = this.updateTransform
    app.addZoomBlocker = this.addBlocker
    app.root = new PIXI.Application({
      backgroundAlpha: 0,
      view: this.canvasRef.current,
      resolution: window.devicePixelRatio,
      autoDensity: true,
      preserveDrawingBuffer: HighlightService.isServiceInitialized() ? true : false,
      antialias: !localStorage.getItem('mcht_disable_antialias'),
      width: this.props.width,
      height: this.props.height,
    })

    // eslint-disable-next-line no-undef
    globalThis.__PIXI_APP__ = app.root

    app.root.stage.id = 'root'

    const selection = d3select(app.root.view)
    const zoom = d3zoom().scaleExtent(appScaleExtent)

    this.addBlocker(() => {
      return window.app._isDragging
    })

    if (isMobile) {
      // Mobile devices: use simpler zoom filter
      zoom.filter(() => {
        for (let { zoomBlockers } = this, i = -1, length = zoomBlockers.length; ++i < length; ) {
          if (zoomBlockers[i]()) {
            return false
          }
        }
        return true
      })
    } else {
      // Desktop devices: use complex zoom filter
      zoom.filter((e) => {
        if (app.state === STATES.PANNING && e.type !== 'wheel') {
          return true
        }

        if (this.isMouseWheelEvent(e)) return true

        // navigation with mouse via left button or middle button
        if (
          !e.metaKey &&
          !e.ctrlKey &&
          e.type === 'mousedown' &&
          (e.button === 0 || e.button === 1)
        ) {
          return true
        }

        // navigation via trackpad
        if (e.type === 'wheel' && !e.ctrlKey && !e.metaKey) {
          return false
        }

        if (e.type === 'dblclick') return false

        for (let { zoomBlockers } = this, i = -1, length = zoomBlockers.length; ++i < length; ) {
          if (zoomBlockers[i]()) {
            return false
          }
        }

        return true
      })
    }

    // Preventing default zoom behavior
    if (!isMobile) {
      this.canvasRef.current.addEventListener('wheel', this.handlePreventDefault, {
        passive: false,
      })

      // Handling navigation with touchpad
      this.canvasRef.current.addEventListener('wheel', this.handleNavigation)
    }

    const handleTransformUpdate = (event) => {
      this.updateTransform()
      if (some(event.transform, isFinite)) {
        this.props.onTransform(event.transform)
      }
    }

    // handle d3 zoom events
    zoom.on('zoom', (event) => {
      app.transform = event.transform
      handleTransformUpdate(event)
      return
    })

    // Use the selection element to handle wheel events for zooming
    selection.call(zoom)

    const { sidebarWidth } = app
    let baseTransform = zoomIdentity
      .translate(sidebarWidth + (this.props.width - sidebarWidth) / 2, this.props.height / 2)
      .scale(0.45)

    if (this.props.initialTransform) {
      const { x, y, k } = this.props.initialTransform
      baseTransform = zoomIdentity.translate(x, y).scale(k)
    }

    if (!Object.values(baseTransform).some(isNaN)) {
      zoom.transform(selection, baseTransform)
    }

    app.selection = selection
    app.zoom = zoom

    this.ticker = app.root.ticker

    // Disabling rendering on every tick
    this.ticker.remove(app.root.render, app.root)

    this.ticker.add(
      (delta) => {
        TWEEN.update()
        this.updateTransform()

        // Enabling conditional rendering
        if (app._render) {
          app.emit('willRender')
          if (this.props.player) {
            this.applyTextResolution()
          } else {
            this.culling()
            this.applyTextResolution(TEXT_RESOLUTION_CHANGES_PER_FRAME_LIMIT)
          }
          app._render = false
          app.root.render()
        }
      },
      null,
      PIXI.UPDATE_PRIORITY.LOW,
    )

    const { stage } = app.root
    this.stage = stage
    stage.interactive = !this.props.readonly
    stage.hitArea = new PIXI.Rectangle(-10e10, -10e10, 10e20, 10e20)

    if (isMobile) {
      // Use mobile touch handlers
      stage.on('touchstart', this.handleStageTouchStart)
      stage.on('touchend', this.handleStageTouchEnd)
      stage.on('touchcancel', this.handleStageTouchEnd)
      stage.on('touchmove', this.handleStageMouseMove)
    } else {
      // Use desktop mouse handlers
      stage.on('mousedown', this.handleStageMouseDown)
      stage.on('mousemove', this.handleStageMouseMove)
    }

    this.setState({ ready: true, offsetX: this.getOffset().x, offsetY: this.getOffset().y })
  }

  componentWillUnmount() {
    app.zoom.on('zoom', null)
    if (!isMobile) {
      this.canvasRef.current.removeEventListener('wheel', this.handlePreventDefault)
      this.canvasRef.current.removeEventListener('wheel', this.handleNavigation)
    }
  }

  handleStageTouchStart = (e) => {
    currentTouchesCount++
    isMultitouch = currentTouchesCount > 1
    e.data.multitouch = isMultitouch
    this.handleStageMouseDown(e)
  }

  handleStageTouchEnd = (e) => {
    currentTouchesCount--
    e.data.multitouch = isMultitouch
  }

  handleStageMouseDown = (e) => {
    e.data.mouseDownTS = Date.now()
    e.data.mouseDownGlobal = e.data.global.clone()
    e.data.mouseDownLocal = e.data.getLocalPosition(this.stage)
    e.data.mouseDragDelta = 0
    e.data.mouseDownGlobal = e.data.global.clone()
  }

  handleStageMouseMove = (e) => {
    if (!e.data.mouseDownGlobal) {
      return
    }
    if (e.data.originalEvent.buttons !== 1 && e.data.originalEvent.type !== 'touchmove') {
      return
    }
    const { mouseDownGlobal, global } = e.data
    const deltaX = Math.abs(mouseDownGlobal.x - global.x)
    const deltaY = Math.abs(mouseDownGlobal.y - global.y)
    e.data.mouseDragDelta = Math.max(e.data.mouseDragDelta, deltaX, deltaY)
  }

  culling = () => {
    const { children } = app.root.stage
    const { width, height } = app.root.screen

    const { x: topLeftX, y: topLeftY } = app.root.stage.toLocal({ x: 0, y: 0 })
    const { x: bottomRightX, y: bottomRightY } = app.root.stage.toLocal({ x: width, y: height })

    const bRect = new PIXI.Rectangle()

    for (let i = 0; i < children.length; i++) {
      const child = children[i]
      if (typeof child.getCullingBounds !== 'function') {
        continue
      }

      child.getCullingBounds(bRect)

      let visible = false
      const childTopLeftX = bRect.x
      const childTopLeftY = bRect.y
      if (childTopLeftX < bottomRightX && childTopLeftY < bottomRightY) {
        const childBottomRightX = childTopLeftX + bRect.width
        const childBottomRightY = childTopLeftY + bRect.height
        visible = childBottomRightX > topLeftX && childBottomRightY > topLeftY
      }

      child.visible = visible
    }
  }

  applyTextResolution = (changeLimit) => {
    const scale = this.props.isStaticTextResolution ? 1 : app.transform.k

    const { remained } = applyTextResolution(texts, scale, changeLimit)

    if (remained > 0) {
      cancelAnimationFrame(this.textResolutionFrameId)
      this.textResolutionFrameId = requestAnimationFrame(app.render)
    }
  }

  canvasRef = React.createRef()

  render() {
    const { ready } = this.state

    return (
      <>
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
          }}
        >
          <canvas ref={this.canvasRef} key="1" id={BUILDER_CANVAS_ID} />
          {ready && this.props.children}
          <StageDidUnmountHandler />
        </div>
      </>
    )
  }

  setupUITests = () => {
    if (isTestEnv()) {
      // wait for next render
      setTimeout(() => {
        window.$stage = this.stage
      }, 100)
    }
  }
}

// Because React call 'componentWillUnmount' handler for parent first, we are using StageDidUnmountHandler
// component as last child, so app.root.destroy will be called after all other stage childs unmounted
class StageDidUnmountHandler extends Component {
  componentWillUnmount() {
    app.root.destroy()
    app.root = null
    app.initialFrameNodeId = null
  }

  render() {
    return null
  }
}

export default PixiStage
